import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { toast } from '#/utils/toast';

function defaultResolveCreatedId(result) {
  return result?.data?.id ?? null;
}

/**
 * Settings create/edit 儲存成功後的共通契約：
 * - create：toast → invalidate list → navigate 編輯頁（或 getDetailPath）
 * - update：toast → invalidate list + detail → 留頁（可選 afterUpdateNavigateTo）
 *
 * 不包 RHF／zod；頁面自管表單。`onError` 在錯誤 toast 後呼叫（例：標示欄位錯誤）。
 */
function useCreateEditSave({
  id = null,
  createFn,
  updateFn,
  buildPayload,
  mutationFn: customMutationFn,
  listQueryKeyPrefix,
  detailQueryKey,
  getEditPath,
  getDetailPath = null,
  resolveCreatedId = defaultResolveCreatedId,
  getSuccessMessage,
  getErrorMessage,
  isVersionConflict,
  onVersionConflict,
  onError: onSaveError,
  afterUpdateNavigateTo = null,
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = Boolean(id);

  const saveMutation = useMutation({
    mutationFn: async (variables) => {
      if (customMutationFn) {
        return customMutationFn(variables);
      }

      const payload = buildPayload(variables, { isEdit, id });
      if (isEdit) {
        return updateFn(id, payload);
      }

      return createFn(payload);
    },
    onSuccess: async (result, variables) => {
      const message = getSuccessMessage
        ? getSuccessMessage(result, { isEdit, variables })
        : (result?.message || (isEdit ? '更新成功' : '建立成功'));

      toast.success(message);
      await queryClient.invalidateQueries({ queryKey: listQueryKeyPrefix() });

      if (isEdit && id) {
        const detailKey = detailQueryKey(id);
        await queryClient.invalidateQueries({ queryKey: detailKey });
        await queryClient.refetchQueries({ queryKey: detailKey });

        if (afterUpdateNavigateTo) {
          navigate(afterUpdateNavigateTo);
        }
        return;
      }

      const createdId = resolveCreatedId(result, variables);
      if (createdId) {
        if (typeof getDetailPath === 'function') {
          navigate(getDetailPath(createdId));
          return;
        }
        if (typeof getEditPath === 'function') {
          navigate(getEditPath(createdId));
        }
      }
    },
    onError: (error) => {
      const message = getErrorMessage
        ? getErrorMessage(error)
        : '儲存失敗';
      toast.error(message);
      onSaveError?.(error);

      if (isEdit && isVersionConflict?.(error)) {
        onVersionConflict?.();
      }
    },
  });

  function save(variables) {
    if (saveMutation.isPending) {
      return;
    }

    saveMutation.mutate(variables);
  }

  return {
    isEdit,
    isPending: saveMutation.isPending,
    save,
    saveMutation,
  };
}

export default useCreateEditSave;
